import { Switch, Route, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/theme-provider";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { Layout } from "@/components/layout";
import { PasswordChangeModal } from "@/components/password-change-modal";
import WelcomePage from "@/pages/welcome";
import PremiumWelcomePage from "@/pages/premium-welcome";
import LoginPage from "@/pages/login";
import QueryPage from "@/pages/query";
import ResearchChatPage from "@/pages/research-chat";

import SmartPromptsPage from "@/pages/smart-prompts";
import TemplatesPage from "@/pages/templates";
import AnalyticsPage from "@/pages/analytics";
import SignalsPage from "@/pages/signals";
import ProfilePage from "@/pages/profile";
import ReportBuilderV1 from "@/pages/ReportBuilderV1";
// Admin routes removed - admin dashboard is separate Railway service
import LoginDemo from "@/pages/login-demo";
import NotFoundPage from "@/pages/not-found";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ component: Component, useLayout = true }: { component: () => JSX.Element; useLayout?: boolean }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (!useLayout) {
    return <Component />;
  }

  return (
    <Layout>
      <Component />
    </Layout>
  );
}

function PublicRoute({ component: Component }: { component: () => JSX.Element }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    return <Redirect to="/" />;
  }

  return <Component />;
}

function Router() {
  const { user, isLoading, requiresPasswordChange, clearPasswordChangeRequirement } = useAuth();

  return (
    <>
      <PasswordChangeModal 
        isOpen={requiresPasswordChange} 
        onPasswordChanged={clearPasswordChangeRequirement} 
      />
      <Switch>
      <Route path="/welcome">
        <PremiumWelcomePage />
      </Route>
      <Route path="/login">
        <PublicRoute component={LoginPage} />
      </Route>
      <Route path="/">
        {isLoading ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Loading...</p>
            </div>
          </div>
        ) : !user ? (
          <Redirect to="/welcome" />
        ) : (
          <ProtectedRoute component={ResearchChatPage} useLayout={false} />
        )}
      </Route>
      <Route path="/query">
        <Redirect to="/" />
      </Route>
      <Route path="/chat">
        <Redirect to="/" />
      </Route>
      <Route path="/dashboard">
        <Redirect to="/" />
      </Route>
      <Route path="/query-legacy">
        <ProtectedRoute component={QueryPage} />
      </Route>
      <Route path="/signals">
        <ProtectedRoute component={SignalsPage} useLayout={false} />
      </Route>
      <Route path="/smart-prompts">
        <ProtectedRoute component={SmartPromptsPage} useLayout={false} />
      </Route>
      <Route path="/templates">
        <ProtectedRoute component={TemplatesPage} useLayout={false} />
      </Route>
      <Route path="/profile">
        <ProtectedRoute component={ProfilePage} useLayout={false} />
      </Route>
      <Route path="/veritus/report-v1">
        <ProtectedRoute component={ReportBuilderV1} useLayout={false} />
      </Route>
      <Route path="/reports">
        <Redirect to="/veritus/report-v1" />
      </Route>
      {/* Admin routes removed - admin dashboard is separate Railway service at admin-dashboard-production-*.railway.app */}
      <Route path="/login-demo">
        <LoginDemo />
      </Route>
      <Route component={NotFoundPage} />
    </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <AuthProvider>
          <TooltipProvider>
            <Router />
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;